import Image from 'next/image';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@/primitives';

export function EbvPercentileGraphInfoDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="ebv-percentile-graph-info-title"
    >
      <DialogTitle
        id="ebv-percentile-graph-info-title"
        component="div"
        variant="headlinesmall"
      >
        About the EBV Percentile Graph
      </DialogTitle>
      <DialogContent dividers sx={{ pt: 2 }}>
        <Stack
          spacing={2}
          sx={{
            mb: 2,
            '& strong': { fontWeight: 700 },
          }}
        >
          <Typography
            variant="bodymedium"
            component="p"
            sx={{ color: 'text.primary', m: 0 }}
          >
            The <strong>EBV Percentile Graph</strong>
            {` `}enables you to assess where an individual animal&apos;s EBVs
            currently rank compared with the two-year-old animals evaluated in
            the same genetic evaluation. Each bar represents a trait, and its
            position along the horizontal scale shows the animal&apos;s
            percentile ranking within the breed, with the direction of each
            trait provided for easy reference. In the example shown, the animal
            ranks in the <strong>Top 10%</strong> of the breed for{' '}
            <strong>400 Day Weight</strong> and <strong>600 Day Weight</strong>,
            but in the <strong>lowest 10th percentile band</strong> (Top 90%)
            for <strong>Milk</strong>.
          </Typography>
          <Typography
            variant="bodymedium"
            component="p"
            sx={{ color: 'text.primary', m: 0 }}
          >
            For many traits, animals toward the right-hand side of the graph are
            generally more desirable as they are in the top proportion of the
            breed. However, it is not always optimal to select animals at the
            extreme left, as the most appropriate percentile depends on breeding
            objectives and production environment. Traits such as{' '}
            <strong>Milk</strong>, <strong>Mature Cow Weight</strong>,{' '}
            <strong>Birth Weight</strong>, <strong>Rib Fat</strong> and{' '}
            <strong>Rump Fat</strong> are examples where selecting animals
            within an <strong>optimal or moderate range</strong> may be
            preferable.
          </Typography>
        </Stack>
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            borderRadius: 2,
            overflow: 'hidden',
            border: 1,
            borderColor: 'divider',
          }}
        >
          <Image
            src="/images/ebv-percentile-graph-example-v2.png"
            alt="Example EBV Percentile Graph: percentile scale 100 to 1, trait bars for growth, milk, and carcass traits with PERC column"
            width={1002}
            height={613}
            sizes="(max-width: 900px) 100vw, 860px"
            style={{ width: '100%', height: 'auto', display: 'block' }}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button variant="contained" onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
